import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as XLSX from 'xlsx';

import { CollaboratorsAnalyticsHelper } from '@core/Analytics/CollaboratorsAnalyticsHelper';
import { UsersService, UserSearchResult } from '@services/UsersService';

export type CollaboratorsTab = 'active' | 'deactivated';

export interface CollaboratorCatalogRow {
    id: string;
    name: string;
    mobilePhone: string;
    dni: string;
    empresa: string;
    address: string;
    email: string;
    localidad: string;
    legajo: string;
    birthDate: string;
    jobTitle: string;
    customFields?: Record<string, string>;
    photoUrl: string;
    deactivated: boolean;
    // Only set on Algolia rows: false when the record is from an old/legacy number
    isCurrentNumber?: boolean;
}

export interface CreateCollaboratorDraft {
    name: string;
    phone: string;
    email: string;
}

export interface CreateCollaboratorErrors {
    name: string;
    phone: string;
    email: string;
}

interface UseColaboradoresScreenViewModelProps {
    orgID: string;
    usersService: UsersService;
    onCreateSuccess?: (collaboratorName: string) => void;
    onCreateError?: (error: unknown) => void;
    onExportError?: (error: unknown) => void;
}

// Active collaborators come from Algolia, page by page
const ACTIVE_PAGE_SIZE = 50;
const SEARCH_DEBOUNCE_MS = 300;

const initialDraft: CreateCollaboratorDraft = {
    name: '',
    phone: '',
    email: '',
};

const initialErrors: CreateCollaboratorErrors = {
    name: '',
    phone: '',
    email: '',
};

const normalizeCollaborators = (rows: any[]): CollaboratorCatalogRow[] => {
    return rows
        .filter((row) => row?.id)
        .map((row) => ({
            id: row.id,
            name: row.name ?? '',
            mobilePhone: row.mobilePhone ?? '',
            dni: row.dni ?? '',
            empresa: row.empresa ?? '',
            address: row.address ?? '',
            email: row.email ?? '',
            localidad: row.localidad ?? '',
            legajo: row.legajo ?? '',
            birthDate: row.birthDate ?? '',
            jobTitle: row.jobTitle ?? '',
            customFields: row.customFields ?? {},
            photoUrl: row.imageURL ?? row.photo ?? '',
            deactivated: Boolean(row.deactivationTimestamp),
        }))
        .sort((left, right) => left.name.localeCompare(right.name, 'es', { sensitivity: 'base' }));
};


const isCurrentNumberHit = (hit: UserSearchResult) => Boolean(hit.mobilePhone) && hit.mobilePhone === hit.id;

const pickOneHitPerCollaborator = (hits: UserSearchResult[]): UserSearchResult[] => {
    const byCollaborator = new Map<string, UserSearchResult>();

    hits.forEach((hit) => {
        const key = hit?.userIDReference;
        if (!key) {
            return;
        }
        // Ghost records: legacy objectIDs (e.g. "whatsapp: 549..." with a space)
        // that no longer match any user and are never re-synced
        if (hit.objectID && hit.id && hit.objectID !== hit.id) {
            return;
        }
        const current = byCollaborator.get(key);
        if (!current || (!isCurrentNumberHit(current) && isCurrentNumberHit(hit))) {
            byCollaborator.set(key, hit);
        }
    });

    return Array.from(byCollaborator.values());
};

// Algolia records are keyed by the whatsapp id; the rest of the app uses the
// main user id (userIDReference), so rows without it can't be edited and are skipped.
const normalizeSearchHits = (hits: UserSearchResult[]): CollaboratorCatalogRow[] => {
    return pickOneHitPerCollaborator(hits)
        .map((hit) => ({
            isCurrentNumber: isCurrentNumberHit(hit),
            id: hit.userIDReference as string,
            name: hit.name ?? '',
            mobilePhone: hit.mobilePhone ?? hit.id ?? '',
            dni: hit.dni ?? '',
            empresa: hit.empresa ?? '',
            address: hit.address ?? '',
            email: hit.email ?? '',
            localidad: hit.localidad ?? '',
            legajo: hit.legajo ?? '',
            birthDate: hit.birthDate ?? '',
            jobTitle: hit.jobTitle ?? '',
            photoUrl: hit.imageURL ?? hit.image ?? '',
            deactivated: false,
        }));
};

// Duplicates can also land on different pages: keep the row in place, but swap
// in the current-number record if the one already shown was a stale one.
const mergeUniqueByID = (current: CollaboratorCatalogRow[], next: CollaboratorCatalogRow[]) => {
    const nextByID = new Map(next.map((row) => [row.id, row]));
    const merged = current.map((row) => {
        const incoming = nextByID.get(row.id);
        return incoming && !row.isCurrentNumber && incoming.isCurrentNumber ? incoming : row;
    });
    const knownIDs = new Set(current.map((row) => row.id));
    return [...merged, ...next.filter((row) => !knownIDs.has(row.id))];
};

const normalizeSearchValue = (value: string) => value.toLowerCase().replace(/\s/g, '');

const normalizePhoneSearchValue = (value: string) => value
    .replace(/^whatsapp:/i, '')
    .replace(/\D/g, '');

const formatDateOnlyForExport = (value: string) => {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
    return match ? `${match[3]}/${match[2]}/${match[1]}` : value;
};

const extractCreatedCollaboratorID = (response: unknown): string => {
    const source = (response as { result?: unknown })?.result ?? response;
    const candidate = source as { id?: unknown; userID?: unknown };
    const id = candidate?.id ?? candidate?.userID;
    return typeof id === 'string' ? id : '';
};

const validateDraft = (
    draft: CreateCollaboratorDraft,
    collaborators: CollaboratorCatalogRow[],
): CreateCollaboratorErrors => {
    const errors = { ...initialErrors };

    if (!draft.name.trim()) {
        errors.name = 'Nombre es requerido';
    } else if (draft.name.trim().length > 40) {
        errors.name = 'Nombre no debe ser mayor a 40 caracteres';
    }

    if (!draft.phone.trim()) {
        errors.phone = 'Celular es requerido';
    } else {
        const normalizedPhone = normalizePhoneSearchValue(draft.phone);
        if (!normalizedPhone) {
            errors.phone = 'Celular no es válido'.localized();
        } else {
            const phoneOwner = collaborators.find((collaborator) => (
                normalizePhoneSearchValue(collaborator.mobilePhone) === normalizedPhone
            ));

            if (phoneOwner) {
                const ownerName = phoneOwner.name || 'otro colaborador';
                errors.phone = phoneOwner.deactivated
                    ? 'Ese número de celular está siendo utilizado por %s, que se encuentra en colaboradores desactivados.'.localized(ownerName)
                    : 'Ese número de celular está siendo utilizado por %s.'.localized(ownerName);
            }
        }
    }

    if (draft.email.trim() && !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(draft.email.trim())) {
        errors.email = 'Email inválido'.localized();
    }

    return errors;
};

export function useColaboradoresScreenViewModel({
    orgID,
    usersService,
    onCreateSuccess,
    onCreateError,
    onExportError,
}: UseColaboradoresScreenViewModelProps) {
    const [creating, setCreating] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [debouncedQuery, setDebouncedQuery] = useState('');
    const [selectedTab, setSelectedTab] = useState<CollaboratorsTab>('active');
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
    const [createDraft, setCreateDraft] = useState<CreateCollaboratorDraft>(initialDraft);
    const [createErrors, setCreateErrors] = useState<CreateCollaboratorErrors>(initialErrors);

    // Active tab (Algolia, paginated)
    const [activeCollaborators, setActiveCollaborators] = useState<CollaboratorCatalogRow[]>([]);
    const [activePage, setActivePage] = useState(0);
    const [activeNbPages, setActiveNbPages] = useState(0);
    const [activeTotal, setActiveTotal] = useState(0);
    const [loadingActive, setLoadingActive] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    // Discards responses from searches that were already replaced by a newer one
    const activeRequestRef = useRef(0);

    // Full roster (getNomina): deactivated tab, export, create validation and detail data
    const [roster, setRoster] = useState<CollaboratorCatalogRow[] | null>(null);
    const [loadingRoster, setLoadingRoster] = useState(false);
    const rosterRef = useRef<CollaboratorCatalogRow[] | null>(null);
    const rosterRequestRef = useRef<Promise<CollaboratorCatalogRow[]> | null>(null);

    const setRosterRows = useCallback((rows: CollaboratorCatalogRow[] | null) => {
        rosterRef.current = rows;
        setRoster(rows);
    }, []);

    const loadRoster = useCallback(async (force = false): Promise<CollaboratorCatalogRow[]> => {
        if (!force && rosterRef.current) {
            return rosterRef.current;
        }
        if (!force && rosterRequestRef.current) {
            return rosterRequestRef.current;
        }

        const request = usersService.getNomina(orgID)
            .then((result: any[]) => {
                const rows = normalizeCollaborators(result ?? []);
                setRosterRows(rows);
                return rows;
            })
            .finally(() => {
                if (rosterRequestRef.current === request) {
                    rosterRequestRef.current = null;
                }
            });

        rosterRequestRef.current = request;
        return request;
    }, [orgID, setRosterRows, usersService]);

    // Drop the cached roster when switching organizations
    useEffect(() => {
        setRosterRows(null);
        rosterRequestRef.current = null;
    }, [orgID, setRosterRows]);

    useEffect(() => {
        const timeout = window.setTimeout(() => {
            setDebouncedQuery(searchQuery.trim());
        }, SEARCH_DEBOUNCE_MS);

        return () => window.clearTimeout(timeout);
    }, [searchQuery]);

    const fetchActiveFirstPage = useCallback(async () => {
        const requestID = ++activeRequestRef.current;
        setLoadingActive(true);
        try {
            const result = await usersService.searchUsers(orgID, debouncedQuery, 0, ACTIVE_PAGE_SIZE);
            if (requestID !== activeRequestRef.current) {
                return;
            }
            setActiveCollaborators(normalizeSearchHits(result.hits));
            setActivePage(result.page);
            setActiveNbPages(result.nbPages);
            setActiveTotal(result.nbHits);
        } catch (error) {
            if (requestID === activeRequestRef.current) {
                console.error(error);
                setActiveCollaborators([]);
                setActivePage(0);
                setActiveNbPages(0);
                setActiveTotal(0);
            }
        } finally {
            if (requestID === activeRequestRef.current) {
                setLoadingActive(false);
            }
        }
    }, [debouncedQuery, orgID, usersService]);

    const loadMore = useCallback(async () => {
        if (loadingActive || loadingMore || activePage + 1 >= activeNbPages) {
            return;
        }

        const requestID = activeRequestRef.current;
        setLoadingMore(true);
        try {
            const result = await usersService.searchUsers(orgID, debouncedQuery, activePage + 1, ACTIVE_PAGE_SIZE);
            if (requestID !== activeRequestRef.current) {
                return;
            }
            setActiveCollaborators((current) => mergeUniqueByID(current, normalizeSearchHits(result.hits)));
            setActivePage(result.page);
            setActiveNbPages(result.nbPages);
            setActiveTotal(result.nbHits);
        } catch (error) {
            console.error(error);
        } finally {
            setLoadingMore(false);
        }
    }, [activeNbPages, activePage, debouncedQuery, loadingActive, loadingMore, orgID, usersService]);

    const reloadRoster = useCallback(async () => {
        setLoadingRoster(true);
        try {
            await loadRoster(true);
        } catch (error) {
            console.error(error);
        } finally {
            setLoadingRoster(false);
        }
    }, [loadRoster]);

    useEffect(() => {
        if (selectedTab === 'active') {
            void fetchActiveFirstPage();
        }
    }, [fetchActiveFirstPage, selectedTab]);

    // The roster is the source of truth for who is active: it is also loaded (quietly)
    // on the active tab to filter out anyone Algolia still lists by mistake
    useEffect(() => {
        if (rosterRef.current) {
            return;
        }
        if (selectedTab === 'deactivated') {
            void reloadRoster();
        } else {
            loadRoster().catch((error) => console.error(error));
        }
    }, [loadRoster, reloadRoster, selectedTab, roster]);

    const refresh = useCallback(async () => {
        if (selectedTab === 'active') {
            await fetchActiveFirstPage();
            return;
        }
        await reloadRoster();
    }, [fetchActiveFirstPage, reloadRoster, selectedTab]);

    const deactivatedCollaborators = useMemo(() => {
        return (roster ?? []).filter((collaborator) => collaborator.deactivated);
    }, [roster]);

    // The deactivated tab keeps filtering locally over the full roster
    const visibleDeactivatedCollaborators = useMemo(() => {
        const normalizedSearchQuery = normalizeSearchValue(searchQuery);
        const normalizedPhoneQuery = normalizePhoneSearchValue(searchQuery);

        if (!normalizedSearchQuery) {
            return deactivatedCollaborators;
        }

        return deactivatedCollaborators.filter((collaborator) => {
            const searchableValues = [
                collaborator.name,
                collaborator.email,
                collaborator.dni,
                collaborator.empresa,
                collaborator.legajo,
                collaborator.localidad,
            ];

            const matchesPhone = normalizedPhoneQuery.length > 0 && normalizePhoneSearchValue(
                collaborator.mobilePhone,
            ).includes(normalizedPhoneQuery);

            return matchesPhone || searchableValues.some((value) => (
                normalizeSearchValue(value).includes(normalizedSearchQuery)
            ));
        });
    }, [deactivatedCollaborators, searchQuery]);

    // Only collaborators the roster considers active in this org (stale whitelist
    // entries can keep deactivated ones in the Algolia results)
    const visibleActiveCollaborators = useMemo(() => {
        if (!roster) {
            return activeCollaborators;
        }
        const activeIDs = new Set(roster.filter((row) => !row.deactivated).map((row) => row.id));
        return activeCollaborators.filter((row) => activeIDs.has(row.id));
    }, [activeCollaborators, roster]);

    const visibleCollaborators = selectedTab === 'active'
        ? visibleActiveCollaborators
        : visibleDeactivatedCollaborators;
    const collaboratorsByTabCount = selectedTab === 'active'
        ? activeTotal
        : deactivatedCollaborators.length;
    const loading = selectedTab === 'active' ? loadingActive : loadingRoster;
    const hasMore = selectedTab === 'active' && activePage + 1 < activeNbPages;

    const skipFirstSearchTrack = useRef(true);

    useEffect(() => {
        if (skipFirstSearchTrack.current) {
            skipFirstSearchTrack.current = false;
            return;
        }

        const timeout = window.setTimeout(() => {
            const trimmedQuery = searchQuery.trim();
            CollaboratorsAnalyticsHelper.trackSearchApplied({
                tab: selectedTab,
                queryLength: trimmedQuery.length,
                hasQuery: trimmedQuery.length > 0,
                resultCount: visibleCollaborators.length,
                totalCount: collaboratorsByTabCount,
            });
        }, 500);

        return () => window.clearTimeout(timeout);
    }, [searchQuery, selectedTab, visibleCollaborators.length, collaboratorsByTabCount]);

    // Algolia syncs asynchronously, so local changes are applied right away
    // instead of re-fetching (which could bring back the old data).
    const updateCollaboratorLocally = useCallback((collaborator: CollaboratorCatalogRow) => {
        const replace = (rows: CollaboratorCatalogRow[]) => rows.map((row) => (
            row.id === collaborator.id ? { ...row, ...collaborator } : row
        ));

        setActiveCollaborators(replace);
        if (rosterRef.current) {
            setRosterRows(replace(rosterRef.current));
        }
    }, [setRosterRows]);

    const applyStatusChange = useCallback((collaborator: CollaboratorCatalogRow, deactivated: boolean) => {
        const updatedRow = { ...collaborator, deactivated };

        if (deactivated) {
            setActiveCollaborators((rows) => rows.filter((row) => row.id !== collaborator.id));
            setActiveTotal((total) => Math.max(total - 1, 0));
        } else {
            setActiveCollaborators((rows) => [updatedRow, ...rows.filter((row) => row.id !== collaborator.id)]);
            setActiveTotal((total) => total + 1);
        }

        if (rosterRef.current) {
            setRosterRows(rosterRef.current.map((row) => (row.id === collaborator.id ? updatedRow : row)));
        }
    }, [setRosterRows]);

    // Algolia rows don't carry every field the detail modal shows, so the
    // full row is taken from the roster before opening it.
    const getCollaboratorDetail = useCallback(async (collaborator: CollaboratorCatalogRow) => {
        if (collaborator.deactivated) {
            return collaborator;
        }

        try {
            const rows = await loadRoster();
            const fullRow = rows.find((row) => row.id === collaborator.id);
            return fullRow ? { ...collaborator, ...fullRow } : collaborator;
        } catch (error) {
            console.error(error);
            return collaborator;
        }
    }, [loadRoster]);

    const openCreateModal = useCallback(() => {
        setCreateDraft(initialDraft);
        setCreateErrors(initialErrors);
        setIsCreateModalOpen(true);
        CollaboratorsAnalyticsHelper.trackCreateModalOpened(selectedTab);
    }, [selectedTab]);

    const closeCreateModal = useCallback(() => {
        setIsCreateModalOpen(false);
        setCreateErrors(initialErrors);
    }, []);

    const updateCreateDraft = useCallback((field: keyof CreateCollaboratorDraft, value: string) => {
        setCreateDraft((currentDraft) => ({
            ...currentDraft,
            [field]: value,
        }));

        setCreateErrors((currentErrors) => (
            currentErrors[field] ? { ...currentErrors, [field]: '' } : currentErrors
        ));
    }, []);

    // Exports the whole tab from the roster, not only the loaded Algolia pages
    const exportCurrentTab = useCallback(async () => {
        const columnOrder = [
            'Colaborador',
            'Celular',
            'Identificación',
            'Email',
            'Empresa',
            'Puesto',
            'Fecha de nacimiento',
            'Legajo',
            'Dirección',
            'Localidad',
        ];

        setExporting(true);
        try {
            const rows = await loadRoster(true);
            const tabRows = rows.filter((row) => (
                selectedTab === 'active' ? !row.deactivated : row.deactivated
            ));

            const formattedData = tabRows.map((row) => ({
                [columnOrder[0]]: row.name,
                [columnOrder[1]]: row.mobilePhone,
                [columnOrder[2]]: row.dni,
                [columnOrder[3]]: row.email,
                [columnOrder[4]]: row.empresa,
                [columnOrder[5]]: row.jobTitle,
                [columnOrder[6]]: formatDateOnlyForExport(row.birthDate),
                [columnOrder[7]]: row.legajo,
                [columnOrder[8]]: row.address,
                [columnOrder[9]]: row.localidad,
            }));

            const worksheet = XLSX.utils.json_to_sheet(formattedData, { header: columnOrder });
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, 'Colaboradores');

            const xlsBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
            const blob = new Blob([xlsBuffer], { type: 'application/octet-stream' });
            const link = document.createElement('a');
            const url = URL.createObjectURL(blob);

            link.href = url;
            link.setAttribute(
                'download',
                `listado_colaboradores${selectedTab === 'deactivated' ? '_desactivados' : ''}.xlsx`,
            );
            link.style.visibility = 'hidden';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);

            CollaboratorsAnalyticsHelper.trackExported(selectedTab, tabRows.length);
        } catch (error) {
            console.error(error);
            onExportError?.(error);
        } finally {
            setExporting(false);
        }
    }, [loadRoster, onExportError, selectedTab]);

    const submitCreateCollaborator = useCallback(async () => {
        // Duplicated phones are checked against the full roster, deactivated included
        let rosterRows: CollaboratorCatalogRow[] = rosterRef.current ?? [];
        try {
            rosterRows = await loadRoster(true);
        } catch (error) {
            console.error(error);
        }

        const validationErrors = validateDraft(createDraft, rosterRows);
        setCreateErrors(validationErrors);

        if (Object.values(validationErrors).some(Boolean)) {
            CollaboratorsAnalyticsHelper.trackCreateFailed('validation');
            return false;
        }

        CollaboratorsAnalyticsHelper.trackCreateSubmitted(Boolean(createDraft.email.trim()));
        setCreating(true);
        try {
            const collaboratorName = createDraft.name.trim();
            const normalizedPhone = normalizePhoneSearchValue(createDraft.phone);
            const response = await usersService.createUser(orgID, {
                name: collaboratorName,
                phoneNumber: `whatsapp:+${normalizedPhone}`,
                email: createDraft.email.trim(),
                expirationDate: 4133991599000,
            });

            setIsCreateModalOpen(false);
            setCreateDraft(initialDraft);
            setCreateErrors(initialErrors);

            // Algolia may not have the new collaborator yet: take it from the roster
            try {
                const updatedRoster = await loadRoster(true);
                const createdRow = updatedRoster.find((row) => (
                    !row.deactivated && normalizePhoneSearchValue(row.mobilePhone) === normalizedPhone
                ));
                if (createdRow) {
                    setActiveCollaborators((rows) => [createdRow, ...rows.filter((row) => row.id !== createdRow.id)]);
                    setActiveTotal((total) => total + 1);
                }
            } catch (error) {
                console.error(error);
            }

            CollaboratorsAnalyticsHelper.trackCreateSucceeded(extractCreatedCollaboratorID(response));
            onCreateSuccess?.(collaboratorName);
            return true;
        } catch (error) {
            const reason = error instanceof Error && error.message === 'USER_LIMIT_REACHED'
                ? 'userLimitReached'
                : 'error';
            CollaboratorsAnalyticsHelper.trackCreateFailed(reason);
            console.error(error);
            onCreateError?.(error);
            return false;
        } finally {
            setCreating(false);
        }
    }, [createDraft, loadRoster, onCreateError, onCreateSuccess, orgID, usersService]);

    return {
        loading,
        loadingMore,
        creating,
        exporting,
        searchQuery,
        selectedTab,
        visibleCollaborators,
        collaboratorsByTabCount,
        hasMore,
        isCreateModalOpen,
        createDraft,
        createErrors,
        setSearchQuery,
        setSelectedTab,
        refresh,
        loadMore,
        updateCollaboratorLocally,
        applyStatusChange,
        getCollaboratorDetail,
        exportCurrentTab,
        openCreateModal,
        closeCreateModal,
        updateCreateDraft,
        submitCreateCollaborator,
    };
}
